import type {
  DetailsCarItemDetails,
  DetailsRedSpan,
  ElementsParameters,
} from '@autobid/ui/types/components/CarDetails'
import type { Ref } from 'vue'
import { formatDate } from '@autobid/ui/utils/date/formatDate'
import { computed, unref } from 'vue'

export function usePrepareDetailsCarData(details: Ref<DetailsCarItemDetails>) {
  const { t } = useI18n()
  const { locale } = useI18n()

  const demurageeFee = computed(() => {
    const display = unref(details).demurrageFee[0] !== undefined

    if (!display) {
      return { display }
    }

    return {
      title: t('car-details.section.demurrage-fee'),
      content: t('car-details.demurrage-fee-desc', {
        date: formatDate(unref(details).demurrageFee[0]),
        price: unref(details).demurrageFee[1],
      }),
      display: unref(details).demurrageFee[0] !== undefined,
    }
  })

  const documents = computed(() => {
    return {
      title: t('car-details.section.documents'),
    }
  })

  const extras = computed(() => {
    const data = unref(details).extras || []
    const display
      = data?.[0]?.length > 0 || data?.[1].filter(Boolean)?.length > 0

    if (!display) {
      return { display }
    }

    const list = data[0]
    const freeText = data[1]
    const freeTextHeading = data[2].vanmossel_header

    return {
      display,
      list,
      freeText,
      freeTextHeading,
      title: t('car-details.section.extras'),
    }
  })

  const foundDefects = computed(() => {
    const amountOfDefects
      = unref(details).foundDefects?.tableCells?.reduce((acc, el) => {
        acc += (el[1] as string[]).length
        return acc
      }, 0) ?? 0
    const display = amountOfDefects > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.found-defects'),
      amountOfDefects,
    }
  })

  const imageAdditionalDetails = computed(() => {
    const display = unref(details).imageAdditionalDetails.length > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.image-additional-details'),
    }
  })

  const imageBeautyDetails = computed(() => {
    const display = unref(details).imageBeautyDetails.length > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.image-beauty-details'),
    }
  })

  const imagePanelInstrument = computed(() => {
    const displayList = unref(details).imagePanelInstrument.list?.length > 0
    const displayImages = unref(details).imagePanelInstrument.images?.length > 0
    const display = displayList || displayImages

    if (!display) {
      return { display }
    }

    const TWO_COLUMNS_FROM = 10

    return {
      display,
      title: t('car-details.section.image-panel-instrument'),
      displayList,
      displayImages,
      twoColumnsFrom: TWO_COLUMNS_FROM,
      renderInTwoColumns:
        (unref(details).imagePanelInstrument.list.length ?? 0)
        > TWO_COLUMNS_FROM,
    }
  })

  const missingParts = computed(() => {
    const [list, freeText] = unref(details).missingParts || [[], []]
    const display = list.length > 0 || freeText.length > 0

    if (!display) {
      return { display }
    }

    const TWO_COLUMNS_FROM = 12

    return {
      display,
      title: t('car-details.section.missing-parts'),
      twoColumnsFrom: TWO_COLUMNS_FROM,
      renderInTwoColumns: (list.length ?? 0) > TWO_COLUMNS_FROM,
      list,
      freeText,
    }
  })

  const seats = computed(() => {
    const displayList = unref(details).seats.list?.length > 0
    const displayTable = unref(details).seats.table?.tableCells.length > 0
    const display = displayList || displayTable

    if (!display) {
      return { display }
    }

    const TWO_COLUMNS_FROM = 12

    return {
      display,
      displayList,
      displayTable,
      title: t('car-details.section.seats'),
      twoColumnsFrom: TWO_COLUMNS_FROM,
      renderInTwoColumns:
        (unref(details).seats.list.length ?? 0) > TWO_COLUMNS_FROM,
    }
  })

  const specialExtras = computed(() => {
    const display = unref(details).specialExtras.extras.length > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.special-extras'),
    }
  })

  const accidentPredamages = computed(() => {
    const display = unref(details).accidentPredamages.length > 0

    if (!display) {
      return { display }
    }

    const elements: ElementsParameters = unref(details)
      .accidentPredamages
      .filter(el => el.length)
      .map((el) => {
        const param = el[0]
        const value = el[1]

        if (!Array.isArray(value)) {
          return [param, value]
        }

        const parsedValue: Array<DetailsRedSpan | string> = value.map(
          (subvalue) => {
            if (
              subvalue
              && typeof subvalue === 'object'
              && 'type' in subvalue
            ) {
              return subvalue
            }

            const isMultilanguageFreeText
              = Array.isArray(subvalue)
                && subvalue.length === 2
                && !subvalue[1].startsWith('http')

            // pick a suitable language for free text
            if (isMultilanguageFreeText) {
              return String(subvalue[locale.value === 'de' ? 0 : 1])
            }

            return subvalue
          },
        )

        return [param, parsedValue]
      })

    return {
      display,
      elements,
      title: t('car-details.section.accident-predamages'),
    }
  })

  const generalInformation = computed(() => {
    const [list, freeText] = unref(details).generalInformation || [[], []]
    const display = list.length > 0 || freeText.length > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.general-information'),
      list,
      freeText,
    }
  })

  const goft = computed(() => {
    const title = unref(details).goft.headline
    const content = unref(details).goft.text

    const display = !!(title?.length && content?.length)

    if (!display) {
      return { display }
    }

    return {
      display,
      title,
      content,
    }
  })

  const startPrice = computed(() => {
    const EMPTY_STATE = [null, null]
    const [priceRow, taxRow] = unref(details).startPrice
    const [priceLabel, priceWithCurrency] = priceRow ?? EMPTY_STATE
    const [taxLabel, tax] = taxRow ?? EMPTY_STATE
    const priceWithoutCurrency = priceWithCurrency.replace(/\D/g, '')

    return {
      priceLabel,
      price: +priceWithoutCurrency,
      taxLabel,
      tax,
    }
  })

  const engineTransmissionTurbo = computed(() => {
    const rows = unref(details).engineTransmissionTurbo

    if (!rows.length)
      return { display: false }

    return {
      display: true,
      rows,
      title: t('car-details.section.engine-transmission-turbo'),
    }
  })

  const accidentInfo = computed(() => {
    const display = unref(details).accidentInfo
    if (!display)
      return { display }

    return {
      display,
      title: t('car-details.section.accident-info-title'),
      desc: t('car-details.section.accident-info-desc'),
    }
  })

  const manufacturerInformation = computed(() => {
    const display
      = Object.values(unref(details).manufacturerInformation ?? {}).filter(
        Boolean,
      ).length > 0

    if (!display) {
      return { display }
    }

    return {
      display,
      title: t('car-details.section.manufacturer-information'),
      data: unref(details).manufacturerInformation,
    }
  })

  return {
    demurageeFee,
    documents,
    extras,
    foundDefects,
    imageAdditionalDetails,
    imageBeautyDetails,
    imagePanelInstrument,
    missingParts,
    seats,
    specialExtras,
    accidentPredamages,
    generalInformation,
    goft,
    startPrice,
    engineTransmissionTurbo,
    accidentInfo,
    manufacturerInformation,
  }
}
